import React, { memo } from 'react';

import { useTheme } from '@material-ui/core';
import { get, isArray } from 'lodash';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { showConfirmDialog } from '../../../actions/messageDialog';
import { TYPES, showModal } from '../../../actions/modal';
import { filterReportsByFlight } from '../../../actions/reports';
import { redirectPage } from '../../../actions/screen';
import { getTelex, showAttachment as showAttachmentTelex } from '../../../actions/telex';
import { Attachment } from '../../../icons';
import { useDefects } from '../../../selectors/defects';
import { useDocuments } from '../../../selectors/documents';
import { getCurrentLeg, getCurrentLegInfo } from '../../../selectors/screen';
import { getTelexInfo } from '../../../selectors/telegrams';
import { List, ListItem, ListItemIcon, ListItemText } from '../../material-ui/List';
import { AirportDocuments } from '../AirportDocuments/AirportDocuments';
import { Limitations } from '../Limitations/Limitations';
import { TelexList } from '../TelexList/TelexList';

import { getStyles, useStyles } from './useStyles';
import { useLanguage } from '../../hooks/useLanguage/useLanguage';

export const AttachmentContent = memo(({ onClose }) => {
    const dispatch = useDispatch();
    const { isEng} = useLanguage()
    const defects = useDefects();
    const documents = useDocuments();
    const {
        currentLegInfo = '',
        telegrams,
        id,
        plnName,
        ofps,
        isNoticeAllowed,
        isNoticePreviewAllowed,
        currentOfpId,
    } = useSelector((state) => {
        const {
            screen: { currentLeg },
            sendedData,
            routesDeclaration,
        } = state;
        const isNoticeAllowed = get(routesDeclaration, 'options.allowNotices', false);
        const isNoticePreviewAllowed = get(routesDeclaration, 'options.allowNoticesPreview', false);
        const leg = getCurrentLeg(state) || {};
        const ofpId = get(sendedData[currentLeg], 'ofpId');
        const currentLegInfo = getCurrentLegInfo(state);
        const { telegramList } = getTelexInfo(state);

        return {
            currentLegInfo: isArray(currentLegInfo) ? currentLegInfo[Number(isEng)] : currentLegInfo,
            telegrams: telegramList,
            plnName: get(leg, 'plnName', ''),
            ofps: get(leg, 'ofps'),
            id: get(leg, 'id'),
            currentOfpId: ofpId,
            isNoticeAllowed,
            isNoticePreviewAllowed,
        };
    }, shallowEqual);
    const theme = useTheme();
    const classes = useStyles();
    const showAttachment = showAttachmentTelex;
    const getCurrentTelegram = getTelex;
    const { t: translate } = useTranslation();

    const styles = React.useMemo(() => {
        return getStyles(theme);
    }, [theme]);

    const ofp = React.useMemo(() => {
        const ofpId = currentOfpId;
        let ofp = null;

        if (!ofps) {
            return ofp;
        }

        Object.keys(ofps).forEach((key) => {
            if (ofps[key].id === +ofpId) {
                ofp = ofps[key];

                return;
            }
        });

        return ofp;
    }, [currentOfpId, ofps]);

    const handleShowConfirmDialog = React.useCallback(
        ({ title, message, btnCancelVisibility = true }) =>
            dispatch(
                showConfirmDialog({
                    title,
                    message,
                    btnCancelVisibility,
                })
            ),
        []
    );

    const handleClickOfp = React.useCallback(() => {
        if (ofp) {
            dispatch(
                getCurrentTelegram({
                    id: ofp.telegramId,
                    name: `OFP ${ofp.name}`,
                })
            );
            onClose();
        } else {
            let msg = {
                title: translate('warningMessage'),
                message: translate('messages.noSelectedCfpForAc'),
                btnCancelVisibility: false,
            };
            handleShowConfirmDialog(msg);
        }
    }, [ofp, onClose]);

    const handleClickSafetyReport = React.useCallback(() => {
        dispatch(filterReportsByFlight(id));
        dispatch(redirectPage('reports'));
        onClose();
    }, [id, onClose]);

    const handleClickNotices = React.useCallback(() => {
        dispatch(
            showModal(TYPES.NOTICE, {
                title: 'NOTAM',
                showPreview: !isNoticeAllowed && isNoticePreviewAllowed,
            })
        );
        onClose();
    }, [isNoticeAllowed, isNoticePreviewAllowed, onClose]);

    const handleClickTelegram = React.useCallback(
        (code) => {
            let telegram;

            Object.keys(telegrams).forEach((key) => {
                if (telegrams[key] && key === code) {
                    const prevMailTime = get(telegram, 'mailTime');
                    const mailTime = get(telegrams[key], 'mailTime');
                    if (!telegram || (prevMailTime && mailTime && new Date(prevMailTime) < new Date(mailTime))) {
                        telegram = telegrams[key];
                    }

                    return;
                }
            });

            if (telegram) {
                dispatch(getCurrentTelegram({ id: telegram.id, name: telegram.name }));
                onClose();
            } else {
                const msg = {
                    title: translate('warningMessage'),
                    message: translate('messages.noTelegramForAc', { code }),
                    btnCancelVisibility: false,
                };
                handleShowConfirmDialog(msg);
            }
        },
        [telegrams, id, onClose]
    );

    const getStyle = React.useCallback(
        (isDisabled, defaultStyle) => {
            const { disabledColor } = theme.palette;
            defaultStyle = defaultStyle || styles.simple;

            return isDisabled ? { ...defaultStyle, color: disabledColor } : defaultStyle;
        },
        [styles]
    );

    const handleShowAttachment = React.useCallback((id) => dispatch(showAttachment(id)), []);

    return (
        <>
            <div className={classes.toolbar} />
            <List className="menu-list" component="div">
                <ListItem className={classes.listItemWithBorderBottom}>
                    <ListItemIcon style={styles.listIcon}>
                        <Attachment width="20" height="20" />
                    </ListItemIcon>
                    <ListItemText
                        primary={`${translate('labels.attachments')} ${currentLegInfo}`}
                        primaryTypographyProps={{ className: classes.bold }}
                    />
                </ListItem>
                <TelexList
                    getStyle={getStyle}
                    telegrams={telegrams}
                    onOfpClick={handleClickOfp}
                    onClickTelegram={handleClickTelegram}
                    onAttachmentClick={handleShowAttachment}
                />
                {isNoticeAllowed || isNoticePreviewAllowed ? (
                    <ListItem className={classes.listItemWithBorder} onClick={handleClickNotices} button={true}>
                        <ListItemText primary={translate('labels.notice')} />
                    </ListItem>
                ) : (
                    ''
                )}
                <ListItem className={classes.listItemWithBorder} onClick={handleClickSafetyReport} button={true}>
                    <ListItemText primary={translate('labels.reports')} />
                </ListItem>
                <Limitations
                    defects={defects}
                    style={styles.listItemWithBorder}
                    primary={
                        <>
                            {translate('labels.acLimitations')}{' '}
                            <span className={classes.bold}>{(plnName && plnName.toUpperCase()) || ''}</span>
                        </>
                    }
                />
                <AirportDocuments docs={documents} style={styles.listItemWithBorder} />
            </List>
        </>
    );
});
